import { type DefaultContext, mutationPromise } from '@egw/clients'
import { createMutationHook, createQueryHook } from '../../../../../hooks'
import {
  type EnrollmentJointDocumentVerificationSubmitInputV2,
  type EnrollmentJointDocumentVerificationDataV2,
  type EnrollmentJointDocumentVerificationSubmitOutputV2,
  type EnrollmentJointDocVerificationCheckStatusInputV2,
} from '../../../../../models/enrollment/joint/document-verification-v2'

/**
 * Get Enrollment Joint DocumentVerification data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointDocumentVerificationDataV2>
 * @example const response = await getEnrollmentJointDocumentVerificationDataV2()
 */
export const getEnrollmentJointDocumentVerificationDataV2 = async (
  context?: DefaultContext,
): Promise<EnrollmentJointDocumentVerificationDataV2> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointDocumentVerificationData',
      query: `mutation getEnrollmentJointDocumentVerificationData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            applicationId
            path
            progress
            documentVerificationId
            documentVerificationStatus
            name {
              firstName
              lastName
            }
          }
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointDocumentVerificationDataV2 }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointDocumentVerificationDataV2 = createQueryHook<
  undefined,
  EnrollmentJointDocumentVerificationDataV2
>(getEnrollmentJointDocumentVerificationDataV2)

/**
 * Submit Enrollment Joint DocumentVerification data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointDocumentVerificationSubmitOutputV2>
 * @example const response = await submitEnrollmentJointDocumentVerificationDataV2();
 */
export const submitEnrollmentJointDocumentVerificationDataV2 = async (
  enrollmentDocumentVerificationSubmitInput: EnrollmentJointDocumentVerificationSubmitInputV2,
  context?: DefaultContext,
): Promise<EnrollmentJointDocumentVerificationSubmitOutputV2> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointDocumentVerificationData',
      query: `mutation submitEnrollmentJointDocumentVerificationData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
            documentVerificationStatus
            documentVerificationId
          }
        }
      }`,
    },
    {
      input: enrollmentDocumentVerificationSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointDocumentVerificationSubmitOutputV2 }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointDocumentVerificationDataV2 = createMutationHook<
  EnrollmentJointDocumentVerificationSubmitInputV2,
  EnrollmentJointDocumentVerificationSubmitOutputV2
>(submitEnrollmentJointDocumentVerificationDataV2)

/**
 * Submit Enrollment Joint DocumentVerificationCheckStatus data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointDocumentVerificationSubmitOutputV2>
 * @example const response = await submitEnrollmentJointDocVerificationCheckStatusDataV2();
 */
export const submitEnrollmentJointDocVerificationCheckStatusDataV2 = async (
  enrollmentDocVerificationCheckStatusInput: EnrollmentJointDocVerificationCheckStatusInputV2,
  context?: DefaultContext,
): Promise<EnrollmentJointDocumentVerificationSubmitOutputV2> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointDocVerificationCheckStatusData',
      query: `mutation submitEnrollmentJointDocVerificationCheckStatusData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
            documentVerificationStatus
          }
        }
      }`,
    },
    {
      input: enrollmentDocVerificationCheckStatusInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointDocumentVerificationSubmitOutputV2 }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointDocVerificationCheckStatusDataV2 = createMutationHook<
  EnrollmentJointDocVerificationCheckStatusInputV2,
  EnrollmentJointDocumentVerificationSubmitOutputV2
>(submitEnrollmentJointDocVerificationCheckStatusDataV2)
